import { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Package, Clock, AlertCircle, Loader2, ShoppingCart } from 'lucide-react';
import { api } from '@/api/apiClient';
import { cur } from '@/lib/currency';
function SuggestionCard({ icon, text, color = 'amber' }) {
  const colors = {
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  };
  return (
    <div className={`flex items-start gap-2 p-3 rounded-xl border text-sm ${colors[color]}`}>
      <span className="text-base flex-shrink-0">{icon}</span>
      <p className="leading-snug">{text}</p>
    </div>
  );
}

export default function OverviewTab({ customer, orders, visits }) {
  const [trend, setTrend] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [loadingTrend, setLoadingTrend] = useState(true);

  useEffect(() => {
    if (!customer?.id) return;
    const load = async () => {
      setLoadingTrend(true);
      try {
        // Fetch up to 100 recent orders and derive both the trend and
        // top products client-side. Saves a round trip.
        const res = await api.get(`/customers/${customer.id}/orders`, { limit: 100 });
        const list = res?.success ? (res.data || []) : [];

        // Trend: group amount_total by month for last 12 months
        const monthMap = new Map();
        for (const o of list) {
          if (!o.date_order) continue;
          const d = new Date(o.date_order);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          monthMap.set(key, (monthMap.get(key) || 0) + Number(o.amount_total || 0));
        }
        const trendArr = Array.from(monthMap.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .slice(-12)
          .map(([month, total]) => ({ month, total, value: total }));
        setTrend(trendArr);

        // Top products: aggregate over lines if present
        const prodMap = new Map();
        for (const o of list) {
          for (const l of (o.lines || o.order_line || [])) {
            const pid = Array.isArray(l.product_id) ? l.product_id[0] : null;
            const pname = Array.isArray(l.product_id) ? l.product_id[1] : (l.name || '');
            if (!pid) continue;
            const row = prodMap.get(pid) || { id: pid, name: pname, qty: 0, revenue: 0 };
            row.qty += Number(l.product_uom_qty || l.quantity || 0);
            row.revenue += Number(l.price_subtotal || 0);
            prodMap.set(pid, row);
          }
        }
        const top = Array.from(prodMap.values())
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, 5);
        setTopProducts(top);
      } catch {
        setTrend([]);
        setTopProducts([]);
      }
      setLoadingTrend(false);
    };
    load();
  }, [customer?.id]);

  // Smart suggestions
  const suggestions = [];
  const daysSinceOrder = customer?.days_since_order;
  const debt = customer?.debit || 0;
  const lastVisit = visits?.[0];

  if (daysSinceOrder > 14) {
    suggestions.push({ icon: '🛒', text: `لم يطلب منذ ${daysSinceOrder} يوم — فرصة للزيارة`, color: 'amber' });
  }
  if (debt > 0) {
    suggestions.push({ icon: '💰', text: `لديه مستحقات بقيمة ${debt.toLocaleString('ar-EG')} ${cur()}`, color: 'red' });
  }
  if (!lastVisit) {
    suggestions.push({ icon: '📍', text: 'لم تُسجَّل زيارة لهذا العميل بعد', color: 'blue' });
  } else {
    const visitDays = Math.floor((Date.now() - new Date(lastVisit.visit_date)) / (1000 * 60 * 60 * 24));
    if (visitDays > 14) {
      suggestions.push({ icon: '📍', text: `آخر زيارة منذ ${visitDays} يوم — يحتاج متابعة`, color: 'amber' });
    }
  }
  if (topProducts.length > 0 && daysSinceOrder > 7) {
    suggestions.push({ icon: '📦', text: `اقترح ${topProducts[0]?.name} في الزيارة القادمة`, color: 'green' });
  }

  return (
    <div className="space-y-5">
      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-bold text-foreground">💡 اقتراحات ذكية</h3>
          {suggestions.map((s, i) => <SuggestionCard key={i} {...s} />)}
        </div>
      )}

      {/* Sales Trend Chart */}
      <div className="bg-card border border-border rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm text-foreground">المبيعات — آخر 12 شهر</h3>
        </div>
        {loadingTrend ? (
          <div className="h-32 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : trend.length === 0 ? (
          <p className="text-center text-muted-foreground text-sm py-8">لا توجد بيانات</p>
        ) : (
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={trend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: 'Cairo' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false}
                tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}ك` : v} />
              <Tooltip
                formatter={(v) => [`${Number(v).toLocaleString('ar-EG')} ${cur()}`, 'المبيعات']}
                contentStyle={{ fontFamily: 'Cairo', fontSize: 11, borderRadius: 10, border: '1px solid hsl(var(--border))' }}
              />
              <Area type="monotone" dataKey="total" stroke="hsl(var(--primary))" fill="url(#salesGrad)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Top Products */}
      {topProducts.length > 0 && (
        <div className="bg-card border border-border rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-4 h-4 text-primary" />
            <h3 className="font-semibold text-sm text-foreground">أكثر المنتجات طلباً</h3>
          </div>
          <div className="space-y-2">
            {topProducts.slice(0, 5).map((p, i) => {
              const maxRev = topProducts[0]?.total_revenue || 1;
              const pct = Math.round((p.total_revenue / maxRev) * 100);
              return (
                <div key={p.product_id} className="flex items-center gap-3">
                  <span className="text-xs font-bold text-muted-foreground w-4">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground line-clamp-1">{p.name}</p>
                    <div className="w-full bg-muted rounded-full h-1 mt-1">
                      <div className="h-1 rounded-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-bold text-primary">{Number(p.total_revenue).toLocaleString('ar-EG', { maximumFractionDigits: 0 })} {cur()}</p>
                    <p className="text-xs text-muted-foreground">{p.total_qty} وحدة</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent orders summary */}
      {orders.length > 0 && (
        <div className="bg-card border border-border rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-primary" />
            <h3 className="font-semibold text-sm text-foreground">آخر الطلبات</h3>
          </div>
          <div className="space-y-2">
            {orders.slice(0, 3).map(o => (
              <div key={o.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                <div>
                  <p className="text-sm font-medium text-foreground">{o.name}</p>
                  <p className="text-xs text-muted-foreground">{new Date(o.date_order).toLocaleDateString('ar-EG', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
                <p className="text-sm font-bold text-primary">{Number(o.amount_total).toLocaleString('ar-EG', { maximumFractionDigits: 0 })} {cur()}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}